import { IScope, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import {
  IAutomation,
  IFlatAutomationTasks,
  IAutomationTask,
  IAutomationAction,
  IAutomationRuleBounds,
  IAutomationRule,
  IAutomationTrigger,
  IAutomationSchedule,
  ICustomFieldProperties,
  IPropertyNames,
  AutomationTriggerType,
  AutomationStatus,
  AutomationOperationByTargetType,
} from "../models/automation";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { AutomationService } from "../services/automation";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/goalService";
import { localize } from "@gtmhub/localization";
import { IStateInit } from "@gtmhub/core/routing";
import { CustomFieldActions, ICustomField, ICustomFieldsState } from "@gtmhub/customFields";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { IPlanningSession, ISessionsState, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { getCustomFieldsByAutomationType, isDeletedCustomField, manuallyMultiplyBy100, isPredefinedRuleValue, checkIfActiveStatusConditionExists } from "../utils/utils";
import { INgRedux } from "@gtmhub/state-management";
import { ICustomFieldsStoreState } from "@gtmhub/customFields";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { RolesActions, IRolesState, IRolesStoreState, IRole } from "@gtmhub/roles";
import { IdMap } from "@gtmhub/util";
import { IGtmhubRootScopeService } from "@gtmhub/models";

interface IAutomationRulesReduxScope {
  assigneeIdMap: IAssigneeIdMap;
  sessions: IPlanningSession[];
  goalCustomFields: ICustomField[];
  roleIdMap: IdMap<IRole>;
  cfState: ICustomFieldsState;
  sessionState: ISessionsState;
  rolesState: IRolesState;
}

const bindStateToScope = (state: ICustomFieldsStoreState & ISessionsStoreState & IAssigneesStoreState & IRolesStoreState): IAutomationRulesReduxScope => {
  return {
    assigneeIdMap: state.assignees.map,
    sessions: state.sessions.items,
    goalCustomFields: state.customFields.map.goal,
    roleIdMap: state.roles.getRolesMapExcludeTeamRoles(),
    cfState: state.customFields,
    sessionState: state.sessions,
    rolesState: state.roles,
  };
};

interface IAutomationRulesRootScopeService extends IGtmhubRootScopeService {
  automationStateName: string;
}

interface IAutomationRulesStateParamsService {
  automationId?: string;
}

export interface IAutomationRulesScope extends IScope, IExtendedRootScopeService, IAutomationRulesReduxScope {
  // must have types
  automation: IAutomation<AutomationTriggerType>;
  assigneeIdMap: IAssigneeIdMap;
  propertiesDictionary;
  ruleDictionary;
  indicators: {
    loading?: IIndicator;
  };
  flattenAutomationTasks: IFlatAutomationTasks[];
  sessions: IPlanningSession[];
  selectionStrings: {
    plural: {
      [key in AutomationTriggerType]: string;
    };
    operationByTriggerType: {
      [triggerType in AutomationTriggerType]: {
        [operation in AutomationOperationByTargetType[triggerType]]: string;
      };
    };
  };

  getPropertyBeautyName(property: string);
  openOption(option: string, params?: { automationId: string; automationTaskId: string; actionId?: string }): void;
  addAction(automationTask: IAutomationTask): void;
  editCondition(automationTask: IAutomationTask): void;
  editAction(automationTask: IAutomationTask, action: IAutomationAction): void;
  deleteAction(automationTask: IAutomationTask, action: IAutomationAction): void;
  deleteCondition(automationTask: IAutomationTask, indexPossition: number): void;
  getAssigneeName(rule: IAutomationRuleBounds): string;
  getRuleBeautyName(rule: IAutomationRuleBounds): string;
  isConditionDisabled(automationTask: IAutomationTask): boolean;
  isActionSupportedByTrigger(action: IAutomationAction): boolean;
  canUserExecuteAction(action: IAutomationAction): boolean;

  getRuleValue(rule: IAutomationRuleBounds): string | number;
  getDictionaryRuleOperator(type: string, rule: IAutomationRuleBounds): string;
}

export class AutomationRulesCtrl implements IStateInit {
  private actionTypes = {
    email: "email",
  };

  private propertyNames: IPropertyNames;
  private customFieldProperties: ICustomFieldProperties;
  private customFieldRules = [];
  private getAssigneesBool = null;

  public static $inject = [
    "$state",
    "$stateParams",
    "$scope",
    "$uibModal",
    "$rootScope",
    "AutomationService",
    "UIErrorHandlingService",
    "GoalService",
    "CustomFieldActions",
    "$ngRedux",
    "PlanningSessionsActions",
    "AssigneeActions",
    "RolesActions",
  ];

  constructor(
    private $state: StateService,
    private $stateParams: IAutomationRulesStateParamsService,
    private $scope: IAutomationRulesScope,
    private $uibModal: IModalService,
    private $rootScope: IAutomationRulesRootScopeService,
    private automationService: AutomationService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalService: GoalService,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private assigneeActions: AssigneeActions,
    private rolesActions: RolesActions
  ) {
    this.$rootScope.$broadcast("$automationStateChange");
    this.$scope.getRuleBeautyName = this.getRuleBeautyName;
    this.$scope.openOption = this.openOption;
    this.$scope.editCondition = this.editCondition;
    this.$scope.editAction = this.editAction;
    this.$scope.deleteAction = this.deleteAction;
    this.$scope.deleteCondition = this.deleteCondition;

    this.$scope.addAction = this.addAction;
    this.$scope.isActionSupportedByTrigger = this.isActionSupportedByTrigger;
    this.$scope.canUserExecuteAction = this.canUserExecuteAction;
    this.$scope.getPropertyBeautyName = this.getPropertyBeautyName;
    this.$scope.propertiesDictionary = this.automationService.getPropertiesDictionary();
    this.$scope.ruleDictionary = this.automationService.getDisplayRuleDictionary();

    this.$scope.isConditionDisabled = this.isConditionDisabled;

    this.$scope.getAssigneeName = this.getAssigneeName;

    this.$scope.selectionStrings = {
      plural: {
        goal: localize("objectives_cap"),
        metric: localize("key_results"),
        user: localize("users_cap"),
      },
      operationByTriggerType: {
        goal: {
          created: localize("goal_is_created"),
          modified: localize("goal_is_edited"),
          deleted: localize("goal_is_deleted"),
          "progress changed": localize("objective_progress_is_updated"),
        },
        metric: {
          created: localize("metric_is_created"),
          modified: localize("metric_is_edited"),
          deleted: localize("metric_is_deleted"),
          "progress changed": localize("metric_progress_is_updated"),
        },
        user: {
          created: localize("user_is_created"),
          deactivated: localize("user_is_deactivated"),
        },
      },
    };

    this.$scope.$on(
      "automationChanged",
      (
        evt,
        options: {
          triggerChanged?: boolean;
          changes?: { status: AutomationStatus; trigger: IAutomationTrigger<AutomationTriggerType>; schedule?: IAutomationSchedule };
        }
      ) => this.init(options)
    );

    this.$scope.getRuleValue = this.getRuleValue;

    this.$scope.indicators = {};

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.getDictionaryRuleOperator = this.getDictionaryRuleOperator;

    this.propertyNames = this.automationService.getConditionPropertyNames();
    this.customFieldProperties = this.automationService.getCustomFieldProperties();
  }

  private getDictionaryRuleOperator = (type: string, rule: IAutomationRuleBounds): string => {
    if (rule.dataType === "date" && isPredefinedRuleValue(rule)) {
      return this.$scope.ruleDictionary.dateLast[rule.operator];
    }
    return this.$scope.ruleDictionary[type][rule.operator];
  };

  private getRuleValue = (rule: IAutomationRuleBounds): string | number => {
    // compare to previous value 'equals' doesn't have a rule.value
    if (rule.useOldValue && rule.operator === "equals") {
      return localize("current_value");
    }

    if (rule.fieldName == "Attainment") {
      return manuallyMultiplyBy100(rule.value);
    }

    if (rule.dataType === "multiselect") {
      return rule.value.join(", ");
    }

    return rule.value;
  };

  private isConditionDisabled = (automationTask: IAutomationTask): boolean => {
    const isManualOrScheduled = this.$scope.automation.trigger.manual || this.$scope.automation.trigger.scheduled;

    if (isManualOrScheduled) {
      return false;
    }

    return this.$scope.automation.trigger.operation !== "progress changed" && automationTask.rules.ruleBounds.some((r) => r.useOldValue);
  };

  public stateInit(): IPromise<unknown> {
    if (this.$stateParams.automationId) {
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
      this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
      this.$ngRedux.dispatch(this.rolesActions.getRolesIfMissing());

      return this.$scope
        .watchUntil(() => this.$scope.sessionState.isFetched && this.$scope.cfState.isFetched && this.$scope.rolesState.isFetched)
        .then(() => this.init());
    }
  }

  private scrollTop = (): void => {
    document.querySelector(".master-container").scrollTo({ top: 0 });
  };

  private init = (options?: {
    triggerChanged?: boolean;
    changes?: { status: AutomationStatus; trigger: IAutomationTrigger<AutomationTriggerType>; schedule?: IAutomationSchedule };
  }): IPromise<unknown> => {
    this.$scope.indicators.loading = { progress: true };

    // triggerTargetType changed => delete automation workflow
    if (options && options.triggerChanged && this.$scope.automation.automationTask) {
      const deletedAutomationTaskId = this.$scope.automation.automationTask.id;
      this.$scope.automation = { ...this.$scope.automation, ...options.changes };
      delete this.$scope.automation.automationTask;
      return this.automationService.deleteAutomationCondition(this.$scope.automation).then(
        () => {
          this.$scope.flattenAutomationTasks = this.$scope.flattenAutomationTasks.filter((task) => task.id !== deletedAutomationTaskId);
          return this.loadAutomation();
        },
        (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true })
      );
    }

    return this.loadAutomation();
  };

  private loadAutomation = (): IPromise<unknown> => {
    return this.automationService.getAutomation(this.$stateParams.automationId).then(
      (automation: IAutomation<AutomationTriggerType>) => {
        this.$scope.automation = automation;
        delete this.$scope.indicators.loading;

        const currentCustomFields = getCustomFieldsByAutomationType(this.$scope.cfState, this.$scope.automation);
        this.$scope.flattenAutomationTasks = this.flattenRecursiveAutomation(this.$scope.automation, currentCustomFields);

        this.$rootScope.$broadcast("currentAutomation", automation);
        this.scrollTop();

        if (this.getAssigneesBool) {
          this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
        }
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private getAssigneeName = (rule: IAutomationRuleBounds): string => {
    if (this.$scope.assigneeIdMap) {
      if (typeof rule.value === "string") {
        return this.$scope.assigneeIdMap[rule.value] ? this.$scope.assigneeIdMap[rule.value].name : rule.value;
      }

      if (Array.isArray(rule.value)) {
        return rule.value
          .map((assignee) => {
            return this.$scope.assigneeIdMap[assignee].name;
          })
          .join(",  ");
      }

      return this.$scope.assigneeIdMap[rule.value] ? this.$scope.assigneeIdMap[rule.value].name : rule.value;
    }
  };

  private getPropertyBeautyName = (property: string) => {
    if (this.$scope.propertiesDictionary[property]) {
      return this.$scope.propertiesDictionary[property];
    }
    return property;
  };

  private getRuleBeautyName = (rule: IAutomationRuleBounds): string => {
    return rule.customFieldDisplayName ? rule.customFieldDisplayName : this.getPropertyBeautyName(rule.fieldName);
  };

  private stateStrings: { trigger: string; condition: string; action: string } = {
    trigger: "._all.trigger",
    condition: "._all.condition",
    action: "._all.action",
  };

  private enrichSessionName = (rule: IAutomationRuleBounds) => {
    this.$scope.sessions.forEach((session) => {
      if (session.id === rule.value) {
        rule.valueName = session.title;
      }
    });
  };

  private checkGetAssignees(automationTask: IAutomationTask): void {
    automationTask.actions.forEach((action) => {
      if (action.actionType === this.actionTypes.email && action.metadata && action.metadata.recipients) {
        this.getAssigneesBool = true;
      }
    });
  }

  // building flatten automationTaskArray
  private flattenRecursiveAutomation = (
    automation: IAutomation<AutomationTriggerType> | IAutomationTask,
    currentCustomFields: ICustomField[],
    flattenAutomationTasks = []
  ): IFlatAutomationTasks[] => {
    // turning off the indicator if there's no automation tasks created yet
    // otherwise it will be stopped by the recursion when it's done enriching data and so on
    // if (!this.$scope.automation.automationTask) {
    //   this.$scope.indicators.loading = false;
    // }

    for (const key in automation) {
      // eslint-disable-next-line no-prototype-builtins
      if (automation.hasOwnProperty(key) && (key === "automationTask" || key === "nextAutomationTask")) {
        // getting values if key is different from default null value
        const automationTask: IAutomationTask = automation[key];
        if (automationTask) {
          if (automationTask.actions.length) {
            this.checkGetAssignees(automationTask);
          }

          flattenAutomationTasks.push({
            id: automationTask.id,
            rules: this.enrichRule(automationTask.rule, currentCustomFields),
            actions: automationTask.actions,
          });
        }

        flattenAutomationTasks.forEach((task) => {
          task.rules.ruleBounds.forEach((rule) => {
            if (this.$scope.sessions.length) {
              this.enrichSessionName(rule);
            }
          });
        });

        this.flattenRecursiveAutomation(automationTask, currentCustomFields, flattenAutomationTasks);

        if (!automationTask && this.customFieldRules.length !== 0) {
          this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
        }
      }
    }

    return flattenAutomationTasks;
  };

  private getGoalAndEnrichRule = (rule: IAutomationRuleBounds, fields = "name") => {
    // if value is missing, the parent okr is empty
    if (!rule.value) {
      return;
    }

    this.$scope.indicators.loading = { progress: true };
    this.goalService.getGoal(rule.value, { fields }).then(
      (goal) => {
        rule.valueName = goal.name;
        delete this.$scope.indicators.loading;
      },
      (error) => (this.$scope.indicators.loading = { error })
    );
  };

  private enrichRule = (rules: IAutomationRule, currentCustomFields: ICustomField[]) => {
    rules.ruleBounds.forEach((rule: IAutomationRuleBounds) => {
      // if the rule is custom field, enriching it with beautyDisplayName
      // and showing deleted custom field text if the custom field isn't persisting anymore
      if (rule.customField) {
        rule.isCustomFieldDeleted = isDeletedCustomField(currentCustomFields, rule);
      }

      if (rule.fieldName === this.propertyNames.parent) {
        rule.dataType = "goal";
        this.getGoalAndEnrichRule(rule);
      }

      if (rule.fieldName === this.propertyNames.session || rule.fieldName === this.propertyNames.goalSessionId) {
        rule.dataType = "session";

        if (this.$scope.sessions.length) {
          this.enrichSessionName(rule);
        }
      }

      if (
        rule.fieldName === this.propertyNames.createdById ||
        rule.fieldName === this.propertyNames.modifiedById ||
        rule.dataType === this.customFieldProperties.selectAssignee ||
        rule.fieldName === this.propertyNames.ownerIds ||
        this.getAssigneesBool
      ) {
        this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
        rule.dataType = "user";
      }

      if (rule.fieldName === this.propertyNames.dateCreated || rule.fieldName === this.propertyNames.dateTo || rule.fieldName === this.propertyNames.dateFrom) {
        rule.dataType = "date";
      }
    });

    return rules;
  };

  private openOption = (option: string, params?: { automationId: string; automationTaskId: string; actionId?: string }): void => {
    switch (option) {
      case "trigger":
        this.$state.go(this.stateStrings.trigger, params);
        break;
      case "condition":
        this.$state.go(this.stateStrings.condition, params);
        break;
      case "action":
        this.$state.go(this.stateStrings.action, params);
        break;
    }
  };

  private editCondition = (automationTask: IAutomationTask): void => {
    this.openOption("condition", { automationId: this.$scope.automation.id, automationTaskId: automationTask.id });
  };

  private editAction = (automationTask: IAutomationTask, action: IAutomationAction): void => {
    this.openOption("action", { automationId: this.$scope.automation.id, automationTaskId: automationTask.id, actionId: action.id });
  };

  private deleteAction = (scopeAutomationTask: IAutomationTask, deleteableAction: IAutomationAction): void => {
    this.automationService.getAutomationTask(this.$scope.automation, scopeAutomationTask.id, (foundAutomationTask: IAutomationTask) => {
      foundAutomationTask.actions = foundAutomationTask.actions.filter((action) => action.id !== deleteableAction.id);

      this.automationService.updateAutomation(this.$scope.automation).then(
        () => {
          scopeAutomationTask.actions = foundAutomationTask.actions;
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  };

  private deleteCondition = (automationTask: IAutomationTask, indexPosition: number): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/confirm-delete.jade"),
      windowClass: "medium",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.automation = ctrl.$scope.automation;

          $scope.close = () => {
            $uibModalInstance.close();
          };
          $scope.confirm = () => {
            $scope.indicators = {
              saving: true,
            };

            ctrl.automationService.getAutomationTask(ctrl.$scope.automation, automationTask.id, (deletedAutomationTask, deletedAutomationTaskAncestorId) => {
              // getting the children of the deleted automationTask
              const deletedAutomationTaskChildren = deletedAutomationTask.nextAutomationTask;

              // if the automationtask is on root level simply swap with the deletedAutomationTaskChildren
              if (indexPosition === 0) {
                ctrl.$scope.automation.automationTask = deletedAutomationTaskChildren;
              } else {
                // or cause recursion to find it and update its ancestor with the deletedAutomationTaskChildren
                ctrl.automationService.getAutomationTask(
                  ctrl.$scope.automation,
                  deletedAutomationTaskAncestorId,
                  (modifiedAutomation) => (modifiedAutomation.nextAutomationTask = deletedAutomationTaskChildren)
                );
              }

              ctrl.automationService.deleteAutomationCondition(ctrl.$scope.automation).then(
                () => {
                  $scope.indicators.saving = false;
                  ctrl.$scope.flattenAutomationTasks = ctrl.$scope.flattenAutomationTasks.filter((task) => task.id !== deletedAutomationTask.id);
                  $uibModalInstance.close();
                },
                (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true })
              );
            });
          };
        },
      ],
    });
  };

  private addAction = (automationTask: IAutomationTask): void => {
    this.openOption("action", { automationId: this.$scope.automation.id, automationTaskId: automationTask.id });
  };

  private isActionSupportedByTrigger = (action: IAutomationAction): boolean => {
    const isExecutedOnUsers = this.$scope.automation.trigger.objectType === "user";
    if (isExecutedOnUsers) {
      const isAutomationTriggeredOnEvent = !this.$scope.automation.trigger.manual && !this.$scope.automation.trigger.scheduled;
      const hasActivStatusCondition = checkIfActiveStatusConditionExists(this.$scope.automation.automationTask);

      if (action.actionType === "delete_item" && (isAutomationTriggeredOnEvent || !hasActivStatusCondition)) {
        return false;
      }

      const isAutomationTriggeredOnDeletedOrDeactivated =
        this.$scope.automation.trigger.operation === "deactivated" || this.$scope.automation.trigger.operation === "deleted";
      if (action.actionType === "assign_role" && isAutomationTriggeredOnEvent && isAutomationTriggeredOnDeletedOrDeactivated) {
        return false;
      }
    }

    return true;
  };

  private canUserExecuteAction = (action: IAutomationAction): boolean => {
    const isExecutedOnUsers = this.$scope.automation.trigger.objectType === "user";
    if (isExecutedOnUsers && (action.actionType === "assign_role" || action.actionType === "unassign_role") && !this.$rootScope.hasPermission("ManageConfiguration")) {
      return false;
    }

    return true;
  };
}
